import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import EditableCard from "./EditableCard";
import { useMsal } from "@azure/msal-react";
import React, { useEffect, useState } from "react";
import useFetching from "../services/cardService";

const AdminCardEditor = () => {
  const { instance } = useMsal();
  const [cards, setCards] = useState([]);
  const [tempCards, setTempCards] = useState([]);
  const [groups, setGroups] = useState([]);
  const { getCards, saveCards } = useFetching();
  const [draggable, setDraggable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  async function loadCards() {
    setIsLoading(true);
    const response = await getCards();
    setCards(response.list);
    setIsLoading(false);
  }

  useEffect(() => {
    loadCards();
  }, [instance]);

  useEffect(() => {
    setTempCards(cards);
  }, [cards]);

  useEffect(() => {
    if (tempCards.length !== 0) {
      const groups = [];
      tempCards.forEach((card) => {
        if (!groups.includes(card.groupId)) {
          groups.push(card.groupId);
        }
      });
      groups.sort((a, b) => {
        return a - b;
      });
      setGroups(groups);
    }
  }, [tempCards]);

  async function handleAdd(id, updatedCard) {
    tempCards.push(updatedCard);
    let updatedCards = tempCards;
    setTempCards(updatedCards);
    await saveCards(tempCards);
    loadCards();
  }

  async function handleUpdate(id, updatedCard) {
    let index = tempCards.findIndex((card) => {
      return card.id === id;
    });
    if (!updatedCard.image) {
      updatedCard.image = "";
    }
    tempCards[index] = updatedCard;
    await saveCards(tempCards);
    loadCards();
  }

  async function handleDelete(id) {
    let newId = 1;
    let updatedCards = [];
    tempCards.forEach((card) => {
      if (card.id !== id) {
        card.id = newId;
        updatedCards.push(card);
        newId++;
      }
    });
    await saveCards(updatedCards);
    loadCards();
  }

  function toggleDraggable() {
    if (draggable) {
      setDraggable(false);
    } else {
      setDraggable(true);
    }
  }

  function arrayMove(arr, fromIndex, toIndex) {
    var element = arr[fromIndex];
    arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, element);
    return arr;
  }

  function handleDragEnd(event) {
    const { active, over } = event;

    if (active.id !== over.id) {
      const activeIndex = tempCards.findIndex((card) => card.id === active.id);
      const activeGroupId = tempCards[activeIndex].groupId;

      if (tempCards.findIndex((card) => card.id === over.id) !== -1) {
        const newIndex = tempCards.findIndex((card) => card.id === over.id);
        const newGroupId = tempCards.find(
          (card) => card.id === over.id
        ).groupId;

        if (activeGroupId === newGroupId) {
          tempCards.find((card) => card.id === active.id).groupId = newGroupId;

          const arr = arrayMove(tempCards, activeIndex, newIndex);
          setTempCards([...arr]);
        }
      }
    }
  }

  async function handleSubmit() {
    await saveCards(tempCards);
    loadCards();
    toggleDraggable();
  }

  function handleAbort() {
    loadCards();
    toggleDraggable();
  }

  return (
    <>
      {isLoading ? (
        <div className="container-big">
          <div className="loading-spinner small"></div>
        </div>
      ) : (
        <div className="admin-edit">
          <div className="card-order">
            {draggable ? (
              <>
                <button className="card-order-button" onClick={handleSubmit}>
                  Reihenfolge speichern
                </button>
                <button className="card-order-button" onClick={handleAbort}>
                  Abbrechen
                </button>
              </>
            ) : (
              <button className="card-order-button" onClick={toggleDraggable}>
                Reihenfolge bearbeiten
              </button>
            )}
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <div className={`container-cards ${draggable ? "draggable" : ""}`}>
              {groups.map((group) => {
                return (
                  <React.Fragment key={group}>
                    {groups.findIndex((temp) => temp === group) !== 0 && (
                      <div className="card-divider"></div>
                    )}
                    <SortableContext
                      items={tempCards.map((card) => {
                        if (card.groupId === group) return card;
                      })}
                      strategy={rectSortingStrategy}
                    >
                      {tempCards &&
                        tempCards.map((card) => {
                          if (card.groupId === group) {
                            return (
                              <EditableCard
                                key={card.id}
                                id={card.id}
                                groupId={card.groupId}
                                image={card.image}
                                title={card.title}
                                description={card.description}
                                link={card.link}
                                onUpdate={handleUpdate}
                                onDelete={handleDelete}
                                isDraggable={draggable}
                              />
                            );
                          }
                        })}
                    </SortableContext>
                  </React.Fragment>
                );
              })}
              <EditableCard
                id={tempCards.length + 1}
                groupId={3}
                title=""
                description=""
                link=""
                onUpdate={handleAdd}
                isAdd={true}
              />
            </div>
          </DndContext>
        </div>
      )}
    </>
  );
};

export default AdminCardEditor;
